import React from 'react';
import {
  Users, FileText, BookOpen, CreditCard, AlertTriangle,
  Check, X, Download, RefreshCw, ClipboardCheck, Eye
} from 'lucide-react';

const OverviewSection = ({
  user,
  mentees,
  courses,
  ebooks,
  payments,
  uploads,
  pendingUploads,
  pendingPayments,
  pendingAppointments,
  atRiskMentees,
  analytics,
  totalRevenue,
  assignmentSubmissions,
  syncingLibrary,
  setActiveSection,
  setMenteeFilter,
  fetchAllData,
  handleSyncLibrary,
  categorizeLoading,
  categorizeResult,
  handleCategorizeLibrary,
  handleDownloadFile,
  handleApproveUpload,
  handleRejectUpload,
  fetchAssignmentSubmissions,
  setSelectedSubmission,
  setShowReviewModal,
  handleMessageMentee,
  handleViewMenteeProfile
}) => {
  return (
    <div className="space-y-6 lg:space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.profile?.first_name || 'Admin'}
          </h1>
          <p className="text-gray-500 text-sm lg:text-base">Here's what needs your attention today</p>
        </div>
        <button 
          onClick={fetchAllData}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Priority Alerts */}
      {(pendingUploads.length > 0 || atRiskMentees.length > 0 || pendingPayments.length > 0 || pendingAppointments.length > 0) && (
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-4 lg:p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 lg:w-6 lg:h-6" />
            <h2 className="text-lg lg:text-xl font-semibold">Needs Your Attention</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <button 
              onClick={() => setActiveSection('appointments')}
              className="bg-white/20 rounded-xl p-3 lg:p-4 text-left hover:bg-white/30 transition-colors"
              data-testid="alert-appointments"
            >
              <p className="text-2xl lg:text-3xl font-bold">{pendingAppointments.length}</p>
              <p className="text-xs lg:text-sm opacity-90">Appointment requests</p>
            </button>
            <button 
              onClick={() => setActiveSection('inbox')}
              className="bg-white/20 rounded-xl p-3 lg:p-4 text-left hover:bg-white/30 transition-colors"
              data-testid="alert-uploads"
            >
              <p className="text-2xl lg:text-3xl font-bold">{pendingUploads.length}</p>
              <p className="text-xs lg:text-sm opacity-90">Uploads awaiting review</p>
            </button>
            <button 
              onClick={() => { setActiveSection('mentees'); setMenteeFilter('at-risk'); }}
              className="bg-white/20 rounded-xl p-3 lg:p-4 text-left hover:bg-white/30 transition-colors"
              data-testid="alert-at-risk"
            >
              <p className="text-2xl lg:text-3xl font-bold">{atRiskMentees.length}</p>
              <p className="text-xs lg:text-sm opacity-90">Mentees at risk</p>
            </button>
            <button 
              onClick={() => setActiveSection('payments')}
              className="bg-white/20 rounded-xl p-3 lg:p-4 text-left hover:bg-white/30 transition-colors"
              data-testid="alert-payments"
            >
              <p className="text-2xl lg:text-3xl font-bold">{pendingPayments.length}</p>
              <p className="text-xs lg:text-sm opacity-90">Pending payments</p>
            </button>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 lg:gap-6">
        <div className="bg-white rounded-xl p-4 lg:p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm text-gray-500">Total Mentees</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{mentees.length}</p>
            </div>
            <div className="w-10 h-10 lg:w-12 lg:h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 lg:w-6 lg:h-6 text-purple-600" />
            </div>
          </div>
          <p className="text-xs lg:text-sm text-green-600 mt-2">
            {analytics?.active_in_last_30_days || 0} active this month
          </p>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-3xl font-bold text-gray-900">£{totalRevenue.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">{payments.filter(p => p.status === 'completed').length} completed payments</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Courses</p>
              <p className="text-3xl font-bold text-gray-900">{courses.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">{ebooks.length} ebooks in library</p>
          <button 
            onClick={handleSyncLibrary}
            disabled={syncingLibrary}
            className="text-sm text-purple-600 mt-1 hover:underline disabled:opacity-50"
          >
            {syncingLibrary ? 'Syncing...' : 'Sync from Drive →'}
          </button>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending Reviews</p>
              <p className="text-3xl font-bold text-gray-900">{pendingUploads.length}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <button 
            onClick={() => setActiveSection('inbox')}
            className="text-sm text-purple-600 mt-2 hover:underline"
          >
            Review now →
          </button>
        </div>
      </div>

      {/* AI Categorization — separate non-destructive panel. Uses Emergent
          LLM key (Claude Haiku) to classify each ebook into Pastor's 7×49
          taxonomy. */}
      <div
        data-testid="library-categorize-panel"
        className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-6"
      >
        <div className="flex items-start gap-3">
          <FileText className="w-6 h-6 text-emerald-600 mt-1 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">
              AI Categorize Library
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Classifies every ebook into the 7-category × 49-subcategory taxonomy (Spiritual Growth, Ministry & Leadership,
              Bible Study & Theology, Revival & Power, Family & Relationships, Christian History & Biography, Practical Christian Life)
              using Claude. <strong>Safe + idempotent</strong> — running again refines existing assignments without destroying any ebooks.
              <span className="block text-xs text-emerald-700 mt-1">
                ✨ Auto-runs in the background after any "Wipe & Re-import" so freshly imported ebooks are categorized within a minute.
              </span>
            </p>

            <div className="flex flex-wrap gap-2 mt-4">
              <button
                onClick={handleCategorizeLibrary}
                disabled={categorizeLoading}
                data-testid="library-categorize-run"
                className="px-5 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg text-base font-bold inline-flex items-center gap-2 shadow-md"
              >
                {categorizeLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25"/>
                      <path fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"/>
                    </svg>
                    Classifying… (~30-60 sec)
                  </>
                ) : (
                  <>Run AI Categorization Now</>
                )}
              </button>
            </div>

            {categorizeResult && (
              <div
                className={`mt-4 border-2 rounded-lg p-4 text-sm ${categorizeResult.ok ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : 'bg-rose-100 border-rose-400 text-rose-900'}`}
                data-testid="library-categorize-result"
              >
                {categorizeResult.ok ? (
                  <>
                    <div className="font-bold text-base mb-2">
                      ✅ Classified {categorizeResult.classified} ebooks
                    </div>
                    {categorizeResult.category_counts && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        {Object.entries(categorizeResult.category_counts)
                          .sort((a, b) => b[1] - a[1])
                          .map(([cat, n]) => (
                            <div key={cat} className="flex justify-between">
                              <span>{cat}</span>
                              <span className="font-semibold">{n}</span>
                            </div>
                          ))}
                      </div>
                    )}
                    {categorizeResult.sample_first_20 && (
                      <details className="mt-3">
                        <summary className="cursor-pointer text-xs font-medium">
                          Show first 20 classifications
                        </summary>
                        <ul className="mt-2 space-y-1 text-xs max-h-48 overflow-auto">
                          {categorizeResult.sample_first_20.map((s, i) => (
                            <li key={i} className="font-mono">
                              <span className="text-gray-700">{s.title}</span>{' '}
                              <span className="text-emerald-700">→ {s.category}</span>{' '}
                              <span className="text-emerald-600">/ {s.subcategory}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </>
                ) : (
                  <>
                    <div className="font-bold text-base mb-1">❌ Categorization failed</div>
                    <div>{categorizeResult.error}</div>
                    {categorizeResult.status && <div className="text-xs mt-1">HTTP status: {categorizeResult.status}</div>}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Uploads - Quick Review */}
      <div className="bg-white rounded-xl border border-gray-100">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Recent Uploads - Quick Review</h2>
          <button 
            onClick={() => setActiveSection('inbox')}
            className="text-purple-600 text-sm font-medium hover:underline"
          >
            View All ({uploads?.length || 0})
          </button>
        </div>
        <div className="divide-y">
          {pendingUploads.slice(0, 5).map((upload) => (
            <div key={upload.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{upload.user_name || upload.user_email}</p>
                  <p className="text-sm text-gray-500">
                    {upload.file_name} • {new Date(upload.uploaded_at).toLocaleDateString()}
                    {upload.file_exists === false && (
                      <span className="ml-2 text-red-500 font-medium">(File unavailable)</span>
                    )}
                  </p>
                </div>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                  {upload.category}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleDownloadFile(upload.file_url, upload.file_name, upload.file_exists)}
                  className={`p-2 rounded-lg ${upload.file_exists === false ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}
                  title={upload.file_exists === false ? "File unavailable" : "Download"}
                  data-testid={`download-${upload.id}`}
                  disabled={upload.file_exists === false}
                >
                  <Download className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => handleApproveUpload(upload.id)}
                  className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 flex items-center gap-1"
                  data-testid={`approve-${upload.id}`}
                >
                  <Check className="w-4 h-4" /> Approve
                </button>
                <button 
                  onClick={() => handleRejectUpload(upload.id)}
                  className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 flex items-center gap-1"
                  data-testid={`reject-${upload.id}`}
                >
                  <X className="w-4 h-4" /> Reject
                </button>
              </div>
            </div>
          ))}
          {pendingUploads.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <Check className="w-12 h-12 mx-auto mb-2 text-green-500" />
              <p>All uploads have been reviewed!</p>
            </div>
          )}
        </div>
      </div>

      {/* Assignment Submissions - Review Section */}
      <div className="bg-white rounded-xl border border-gray-100" data-testid="assignment-submissions-section">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-orange-500" />
            <h2 className="text-lg font-semibold text-gray-900">Assignment Submissions</h2>
            {assignmentSubmissions.filter(s => s.status === 'pending_review' || !s.review_status).length > 0 && (
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                {assignmentSubmissions.filter(s => s.status === 'pending_review' || !s.review_status).length} pending
              </span>
            )}
          </div>
          <button
            onClick={fetchAssignmentSubmissions}
            className="p-2 hover:bg-gray-100 rounded-lg"
            title="Refresh submissions"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="divide-y max-h-[400px] overflow-y-auto">
          {assignmentSubmissions
            .filter(s => s.status === 'pending_review' || !s.review_status)
            .slice(0, 10)
            .map((submission) => (
              <div key={submission.submission_id || submission.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">
                        {submission.mentee_name || submission.mentee_email || 'Unknown User'}
                      </span>
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                        {submission.assignment_title || 'Assignment'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {submission.submission_notes || 'No notes provided'}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>
                        Submitted: {new Date(submission.submitted_at || submission.created_at).toLocaleDateString()}
                      </span>
                      {submission.attachment_url && (
                        <span className="flex items-center gap-1 text-blue-600">
                          <FileText className="w-3 h-3" /> Has attachment
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedSubmission(submission);
                      setShowReviewModal(true);
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
                    data-testid={`review-submission-${submission.submission_id || submission.id}`}
                  >
                    Review
                  </button>
                </div>
              </div>
            ))}
          {assignmentSubmissions.filter(s => s.status === 'pending_review' || !s.review_status).length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <Check className="w-12 h-12 mx-auto mb-2 text-green-500" />
              <p>All assignment submissions have been reviewed!</p>
            </div>
          )}
        </div>
      </div>

      {/* At-Risk Mentees Quick View */}
      {atRiskMentees.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="p-6 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Mentees Needing Attention ({atRiskMentees.length})
            </h2>
            <button 
              onClick={() => { setActiveSection('mentees'); setMenteeFilter('at-risk'); }}
              className="text-purple-600 text-sm font-medium hover:underline"
            >
              View All
            </button>
          </div>
          <div className="divide-y">
            {atRiskMentees.slice(0, 5).map((mentee) => (
              <div key={mentee.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-red-600 font-medium">
                      {mentee.name?.charAt(0) || mentee.email?.charAt(0) || 'M'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{mentee.name || 'Unknown'}</p>
                    <p className="text-sm text-gray-500">
                      Last active: {mentee.last_active ? new Date(mentee.last_active).toLocaleDateString() : 'Never'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleMessageMentee(mentee.id, mentee.name)}
                    className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
                  >
                    Reach Out
                  </button>
                  <button 
                    onClick={() => handleViewMenteeProfile(mentee.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                    data-testid={`view-profile-${mentee.id}`}
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewSection;
